import { defineComponent as _defineComponent } from 'vue'
import { trackEvent } from '@@/bits/analytics'
import { tailwindSurfaceFontClass } from '@@/bits/font'
import { __ } from '@@/bits/intl'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceOnboardingDemoPadletAnimationsStore } from '@@/pinia/surface_onboarding_demo_padlet_animations_store'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfaceQrCodeStore } from '@@/pinia/surface_qr_code'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import SurfaceBuilder from '@@/vuecomponents/SurfaceBuilder.vue'
import SurfaceTitleDescription from '@@/vuecomponents/SurfaceTitleDescription.vue'
import ProcessedImage from '@@/vuecomponents/processed_image.vue'
import { SettingsSubpanel } from '@@/vuexstore/modules/surface_settings'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'SurfaceTitle',
  props: {
    xBuilder: { type: Boolean, default: true },
    xDescription: { type: Boolean, default: true }
  },
  emits: ['title-changed', 'description-changed', 'portrait-changed'],
  setup(__props: any, { emit }) {

const props = __props as { xBuilder: boolean, xDescription: boolean };


const { isDarkMode: browserDarkMode } = storeToRefs(useDarkModeStore())
const { isContainerSmallerThanTabletLandscape } = storeToRefs(useSurfaceContainerSizeStore())
const surfaceStore = useSurfaceStore()
const { title, description, portrait, backgroundLuminance, colorScheme, fontId, isMap, isEmbedded, links } =
  storeToRefs(surfaceStore)

const { pinnedQrCodeUrl, xPinnedQrCode } = storeToRefs(useSurfaceQrCodeStore())

const { setIsTitleClicked, setIsDescriptionClicked } = useSurfaceSettingsStore()

const surfacePermissionsStore = useSurfacePermissionsStore()
const { canIAdminister } = storeToRefs(surfacePermissionsStore)

const surfaceSettingsStore = useSurfaceSettingsStore()
const { showSettingsPanel } = surfaceSettingsStore

const PORTRAIT_SIZE = 48



const isDarkMode = computed(() => {
  if (isContainerSmallerThanTabletLandscape.value && !isEmbedded.value) return browserDarkMode.value
  return isMap.value ? colorScheme.value === 'dark' : backgroundLuminance.value === 'dark'
})

const fontClass = computed<string>(() => {
  return tailwindSurfaceFontClass(fontId.value)
})

watch(title, (): void => {
  emit('title-changed')
})

watch(description, (): void => {
  emit('description-changed')
})

watch(portrait, (): void => {
  emit('portrait-changed')
})

const canModifyWallSettings = computed(() => !isEmbedded.value && canIAdminister.value)

const tryModify = (settingsSubpanel: SettingsSubpanel): void => {
  if (canModifyWallSettings.value) {
    showSettingsPanel(settingsSubpanel)
  }
}

const handleTitleClick = (): void => {
  setIsTitleClicked(true)
  tryModify(SettingsSubpanel.Main)
}

const handleDescriptionClick = (): void => {
  setIsDescriptionClicked(true)
  tryModify(SettingsSubpanel.Main)
}

const tryOpeningIconSettingPanel = (): void => {
  if (surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop) {
    return
  }
  tryModify(SettingsSubpanel.Icon)
}

// Clip path logic

const surfaceDemoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()

const {
  hasClickedTopicText,
  xTopicText,
  pulsateTopicText,
  xTopicEmojiBackground,
  xTopicEmoji,
  isTitleContainerExpanded,
} = storeToRefs(useSurfaceOnboardingDemoPadletAnimationsStore())
const { startTopicAnimation } = useSurfaceOnboardingDemoPadletAnimationsStore()

const surfaceTitleRef = ref<HTMLElement | null>(null)
const surfaceDescriptionComponent = ref<typeof SurfaceTitleDescription | null>(null)
const surfaceDescriptionRef = computed(() => {
  if (surfaceDescriptionComponent.value) {
    return surfaceDescriptionComponent.value.$el
  }
  return null
})

const demoPadletTitleButtonContainerWidth = ref('')

const updateDemoPadletTitleButtonContainerWidth = () => {
  if (surfaceDescriptionRef.value) {
    const baseWidth = surfaceDescriptionRef.value.offsetWidth
    // we add extra width here to make the animation smoother
    demoPadletTitleButtonContainerWidth.value = `${
      isTitleContainerExpanded.value ? baseWidth + 200 : baseWidth + 100
    }px`
  }
}

const realBackgroundLuminance = computed(() => {
  if (isMap.value) {
    return colorScheme.value
  }
  return backgroundLuminance.value
})

const handleTitleAndDescriptionClick = (): void => {
  if (!hasClickedTopicText.value) {
    trackEvent('Demo padlet onboarding', 'Clicked on title and description')
    startTopicAnimation()
  }
}

watch(
  [surfaceDescriptionRef, hasClickedTopicText],
  ([newRef]) => {
    if (newRef) {
      updateDemoPadletTitleButtonContainerWidth()
    }
  },
  { immediate: true },
)

onMounted(() => {
  updateDemoPadletTitleButtonContainerWidth()
})

return { __sfc: true,props, browserDarkMode, isContainerSmallerThanTabletLandscape, surfaceStore, title, description, portrait, backgroundLuminance, colorScheme, fontId, isMap, isEmbedded, links, pinnedQrCodeUrl, xPinnedQrCode, setIsTitleClicked, setIsDescriptionClicked, surfacePermissionsStore, canIAdminister, surfaceSettingsStore, showSettingsPanel, PORTRAIT_SIZE, emit, isDarkMode, fontClass, canModifyWallSettings, tryModify, handleTitleClick, handleDescriptionClick, tryOpeningIconSettingPanel, surfaceDemoPadletPanelStore, hasClickedTopicText, xTopicText, pulsateTopicText, xTopicEmojiBackground, xTopicEmoji, isTitleContainerExpanded, startTopicAnimation, surfaceTitleRef, surfaceDescriptionComponent, surfaceDescriptionRef, demoPadletTitleButtonContainerWidth, updateDemoPadletTitleButtonContainerWidth, realBackgroundLuminance, handleTitleAndDescriptionClick, __, ImageThumbnail, OzContainedButton, OzContainedButtonColorScheme, OzContainedButtonSizePreset, SurfaceBuilder, SurfaceTitleDescription, ProcessedImage }
}

})